import { useStepHasDisabledStatus } from "@/frontend/hooks/useStepHasDisabledStatus";
import { useGlobalStore } from "@/frontend/store/GlobalStore";
import { useParameterStore } from "@/frontend/store/ParameterStore";
import { useTreeStore } from "@/frontend/store/TreeStore";
import { I18n } from "@/i18n/I18n";
import { Status } from "@/types/containerTypes";
import { CONTEXT_LOG_SUBJECT_TYPES } from "@/types/contextLogTypes";
import { ParameterType } from "@/types/parameterTypes";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import { StepFailureModal } from "./StepFailureModal";
import { Step } from "@/backend/models/containers/step";
import { ContextLogInput } from "../contextLogs/ContextLogInput";
import { Button, Modal } from "react-bootstrap";
import { Operation } from "@/backend/models/operation";
import { useGetIncompleteOperations } from "@/frontend/hooks/useGetIncompleteOperations";
import { Parameter } from "@/backend/models/parameter";
import { StepRetryModal } from "./StepRetryModal";

export function StepStatusButtons(
  props: {
    step: Step,
    onUpdateStatus: () => void
  }) {
  const { step, onUpdateStatus } = props;
  const selectedScenario = step.selectedScenarioId;
  const runningCodeOperations = useTreeStore(useShallow((state) => state.operations.filter((operation) => operation.code?.returnCode === 100)));
  const addContextLog = useParameterStore(useShallow((state) => state.addContextLog));
  const updateStatus = useTreeStore(state => state.updateStatus);
  const userInfo = useGlobalStore(useShallow((state) => state.userClass));
  const stepOutputParameters = useParameterStore(useShallow(state => state.parametersForSubjectAndType(step.id, ParameterType.OUTPUT)));
  const validationsPassing = useParameterStore(useShallow(state => state.parameterValidationsPassing(stepOutputParameters.map(p => p.id))));
  const failButtonDisabled = useStepHasDisabledStatus(step.id) || runningCodeOperations.length > 0;
  const disabled = failButtonDisabled || !validationsPassing;
  const [showModal, setShowModal] = useState(false);
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState('');
  const [userEmail, setUserEmail] = useState(userInfo?.email || '');
  const [userEmailError, setUserEmailError] = useState('');

  const { incompleteFileOperations, incompleteCodeOperations, erroredCodeOperations, imcompleteStepInputParameters, incompleteFieldInputs } = useGetIncompleteOperations(selectedScenario);

  const shouldWarnComplete = incompleteFileOperations.length > 0 || incompleteCodeOperations.length > 0 || erroredCodeOperations.length > 0 || imcompleteStepInputParameters.length > 0 || incompleteFieldInputs.length > 0;

  const handleUpdateStatus = async (status: Status) => {
    await updateStatus(step, status);
    onUpdateStatus();
  };

  const handleConfirm = () => {
    if (!note || !userEmail) {
      if (!note || note.length === 0) {
        setNoteError(I18n.get('reasonForOverrideRequired'));
      }
      if (!userEmail || userEmail.length === 0) {
        setUserEmailError(I18n.get('userEmailRequired'));
      }
      return;
    }
    void handleUpdateStatus(Status.PASSED);
    void addContextLog({
      subjectId: step.id,
      subjectType: CONTEXT_LOG_SUBJECT_TYPES.STEP,
      note,
      userEmail,
    });
    clearForm();
    setShowModal(false);
  };

  const onClick = () => {
    if (shouldWarnComplete) {
      setShowModal(true);
    } else {
      void handleUpdateStatus(Status.PASSED);
    }
  };

  const onCancel = () => {
    clearForm();
    setShowModal(false);
  };

  const clearForm = () => {
    setNote('');
    setNoteError('');
    setUserEmail(userInfo?.email || '');
    setUserEmailError('');
  };

  return (
    <>
      <div className='d-flex gap-2'>
        <button className='btn btn-sm btn-success' onClick={onClick} disabled={disabled} type="button">{I18n.get('completeStep')}</button>
        <StepRetryModal stepId={step.id} isDisabled={failButtonDisabled} />
        <StepFailureModal stepId={step.id} isDisabled={failButtonDisabled} onUpdateStatus={onUpdateStatus} />
      </div>

      {showModal && (
        <Modal show onHide={onCancel}>
          <Modal.Header className="bg-danger text-white">
            <Modal.Title>{I18n.get('confirmCompleteStep')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <LinkList items={incompleteFileOperations} label={I18n.get('operationsWithMissingFiles')} setShowConfirmModal={setShowModal} />
            <LinkList items={incompleteCodeOperations} label={I18n.get('codeOperationsWithoutOutputs')} setShowConfirmModal={setShowModal} />
            <LinkList items={erroredCodeOperations} label={I18n.get('codeOperationsWithErrors')} setShowConfirmModal={setShowModal} />
            <LinkList items={imcompleteStepInputParameters} label={I18n.get('stepInputParametersWithMissingFields')} setShowConfirmModal={setShowModal} />
            <LinkList items={incompleteFieldInputs} label={I18n.get('requiredFieldInputsWithMissingValues')} setShowConfirmModal={setShowModal} />
            <p>{I18n.get('completeStepWarning')}</p>
            <ContextLogInput note={note} setNote={setNote} noteError={noteError} userEmail={userEmail} setUserEmail={setUserEmail} userEmailError={userEmailError} contextLogLabelText={I18n.get('reasonForOverride')} />
          </Modal.Body>
          <Modal.Footer>
            <Button variant='secondary' onClick={onCancel}>{I18n.get('cancel')}</Button>
            <Button variant='danger' onClick={handleConfirm}>{I18n.get('confirmStepOverride')}</Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
}

function LinkList(props: {items: Operation[] | Parameter[], label: string, setShowConfirmModal: (b: boolean) => void}) {
  const {items, label, setShowConfirmModal} = props;

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="pb-1">
      <h6>{label}</h6>
      <ul className="list-unstyled">
        {items.map((item) => (
          <li key={item.id} className="link-primary">
            <Link to={`#${item.id}`} className="text-decoration-none" onClick={() => setShowConfirmModal(false)}>
              • {item.title}
            </Link>
          </li>
        ))}
      </ul>
  </div>
  );
}

